import React, {useEffect, useState} from 'react';
import {useRouter} from "next/router";
import axios from "axios";
import {SERVICES} from "../../../common/constants/routes";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import {CmActionType} from "../../types";

interface Props {
    cmAction: CmActionType
    csrfToken: string
}


function C077LobsterRequestSimpleAction(props: Props) {
    const router = useRouter();
    const [lobsterData, setLobsterData] = useState('');
    const [error, setError] = React.useState('');
    const {t} = useTranslation();
    const actionConfiguration = props.cmAction?.actionSettings?.actionConfiguration;
    const errorMessage = actionConfiguration.errorMessage || t("lobster_request.error_message");

    const mandatoryParametersPresent = React.useCallback(() => {
        const mandatoryCheck = (parameter: string) => router.query[parameter];
        return actionConfiguration.mandatoryRequestParameters?.split(',').every(mandatoryCheck);
    }, [router.query, actionConfiguration.mandatoryRequestParameters]);

    const addRequestParameters = React.useCallback((requestData: any) => {
        actionConfiguration.mandatoryRequestParameters?.split(',').forEach((parameter: any) => {
            router.query[parameter] &&
            (requestData[parameter] = encodeURI(router.query[parameter] as string));
        });
        actionConfiguration.optionalRequestParameters?.split(',').forEach((parameter: any) => {
            router.query[parameter] &&
            (requestData[parameter] = encodeURI(router.query[parameter] as string));
        });
        Object.assign(requestData, actionConfiguration.actionParameters!);
    }, [router.query, actionConfiguration.mandatoryRequestParameters, actionConfiguration.optionalRequestParameters, actionConfiguration.actionParameters]);

    const retrieveData = React.useCallback((requestData: any, receivedData: boolean) => {
        if (!receivedData && mandatoryParametersPresent()) {
            axios({
                method: "post",
                url: SERVICES.LOBSTER_REQUEST,
                headers: {
                    'X-CSRF-Token': props.csrfToken
                },
                data: requestData,
            }).then(function (success) {
                setLobsterData(success.data.responseBody);
            }).catch(function (error) {
                console.error("Lobster request failed:", error);
                setError(errorMessage);
            });
        }
    }, [mandatoryParametersPresent, props.csrfToken, errorMessage]);

    useEffect(() => {
        if (!mandatoryParametersPresent()) {
            setError(errorMessage);
        }
    }, [mandatoryParametersPresent, errorMessage]);

    useEffect(() => {
        let receivedData = false;
        const requestData: any = {
            csrfToken: props.csrfToken,
            id: props.cmAction.id,
        }
        addRequestParameters(requestData);
        retrieveData(requestData, receivedData);
        return () => {
            receivedData = true;
        }
    }, [props.csrfToken, props.cmAction.id, addRequestParameters, retrieveData]);

    const replacePlaceholder = React.useMemo((): any[] => {
        if (!lobsterData) return [];
        try {
            const richTextsArray: any[] = [];
            const parsedData = JSON.parse(lobsterData);
            if (!parsedData?.responseData) return [];

            Object.values(parsedData.responseData).map((responseItem: any) => {
                let replacedText = JSON.stringify(props.cmAction.teaserText?.textAsTree);
                Object.entries(responseItem).forEach(([key, value]) => {
                    const keyPlaceholder = "${" + key + "}";
                    replacedText = replacedText.replaceAll(keyPlaceholder, value as string);
                });
                const invalidParamsPattern = /\${(.+?)}/g;
                replacedText = replacedText.replaceAll(invalidParamsPattern, "");
                richTextsArray.push(JSON.parse(replacedText));
            });
            return richTextsArray;
        } catch (e) {
            console.error("Error parsing lobsterData", e);
            return [];
        }
    }, [lobsterData, props.cmAction.teaserText?.textAsTree]);

    const errorElement = React.useMemo(() => (
        error ? <Typography variant="h6" color="error">{errorMessage}</Typography> : null
    ), [error, errorMessage]);

    return <CCOComponentWrapper>
        <CCOContainer key={props.cmAction.id}>
            {errorElement || <></>}
            {lobsterData ? (
                <>
                    {replacePlaceholder.map(
                        (richText: any, index: number) => <CCORichText metadataId={props.cmAction.id}
                                                                       key={index}
                                                                       textAsTree={richText}/>)}
                </>
            ) : <></>}
        </CCOContainer>
    </CCOComponentWrapper>;
}

export default C077LobsterRequestSimpleAction;